import useAuth from '@app/auth/use-auth';
import ComparisonPage from '@app/components/comparisons/comparison-page';
import PageErrorForbidden from '@app/components/layout/page/components/page-error-forbidden';
import { CustomRoute } from '@app/components/layout/sidebar/components/route';
import ProductResult from '@app/components/product-form/product-result';
import PackagingListPage from '@app/pcf/packaging/packaging-list-page';
import ProcessingPage from '@app/pcf/processing/processing-list-page';
import BulkImportPage from '@app/pcf/product/bulk-import/pages/bulk-import-page';
import CreateProductPage from '@app/pcf/product/create-product-page';
import EditProductRoute from '@app/pcf/product/edit-product-route';
import ProductDetailPage from '@app/pcf/product/product-detail/product-detail-page';
import ProductPdfDownloadPage from '@app/pcf/product/product-detail/pdf/product-pdf-download-page';
import ProductsPage from '@app/pcf/product/product-list-page';
import { FEATURES } from '@app/utils/constants';
import { Outlet, Route } from 'react-router-dom';
import CollectionDetailIngredients from './collections/collection-detail/collection-detail-ingredients';
import CollectionDetailPage from './collections/collection-detail/collection-detail-page';
import CollectionDetailProducts from './collections/collection-detail/collection-detail-products';
import CollectionDetailSummary from './collections/collection-detail/collection-detail-summary';
import CollectionListPage from './collections/collection-list-page';
import CreateCollectionPage from './collections/create-collection-page';
import EditCollectionPage from './collections/edit-collection-page';
import ProductDashboardPage from './dashboard/product-dashboard-page';
import IngredientDetailContext from './ingredients/ingredient-detail-context';
import IngredientDetailLayout from './ingredients/ingredient-detail-layout';
import IngredientDetailPage from './ingredients/pages/ingredient-detail-page';
import IngredientsListPage from './ingredients/pages/ingredients-list-page';
import PcfAccessPage from './pcf-access-page';
import ProductComponentsLayout from './product-components/product-components-layout';
import TransportPage from './transport/transport-list-page';

export const pcfRoutes = {
  PCF_ACCESS: '/products/access',
  PRODUCT_DASHBOARD: '/products/dashboard',
  PRODUCTS: '/products',
  PRODUCTS_CREATE: '/products/create',
  PRODUCTS_QUICK_IMPORT: '/products/quick-import',
  PRODUCT_DETAIL: '/products/:product_id',
  PRODUCT_RESULT: '/products/:product_id/result',
  PRODUCT_EDIT: '/products/:product_id/edit',
  PRODUCT_PDF_DOWNLOAD: '/products/:product_id/pdf-download',

  COLLECTIONS: '/collections',
  COLLECTION_DETAIL: '/collections/:collection_id',
  COLLECTION_EDIT: '/collections/:collection_id/edit',
  COLLECTIONS_CREATE: '/collections/create',
  COLLECTION_PRODUCTS: '/collections/:collection_id/products',
  COLLECTION_INGREDIENTS: '/collections/:collection_id/ingredients',

  PRODUCT_COMPONENTS: '/product/components',
  PRODUCT_COMPONENTS_INGREDIENTS: '/product/components/ingredients',
  PRODUCT_COMPONENTS_INGREDIENTS_DETAIL:
    '/product/components/ingredients/:ingredient_uuid',
  PRODUCT_COMPONENTS_PROCESSING: '/product/components/processing',
  PRODUCT_COMPONENTS_PACKAGING: '/product/components/packaging',
  PRODUCT_COMPONENTS_TRANSPORT: '/product/components/transport',
  PRODUCT_COMPARISON: '/product-comparison',
};

export function PCFRoutes() {
  const { user } = useAuth();
  const productOnlyRoute = (element: JSX.Element) =>
    user?.productCategory === 'product' ? element : <PageErrorForbidden />;

  return (
    <>
      <Route
        element={
          <CustomRoute
            feature={[FEATURES.pcf]}
            hasSidebar
            element={<Outlet />}
          />
        }
      >
        <Route path={pcfRoutes.PCF_ACCESS} element={<PcfAccessPage />} />
        <Route
          path={pcfRoutes.PRODUCT_DASHBOARD}
          element={<ProductDashboardPage />}
        />
        <Route path={pcfRoutes.PRODUCTS} element={<ProductsPage />} />
        <Route
          path={pcfRoutes.PRODUCTS_CREATE}
          element={<CreateProductPage />}
        />
        <Route
          path={pcfRoutes.PRODUCT_DETAIL}
          element={<ProductDetailPage />}
        />
        <Route
          path={pcfRoutes.PRODUCT_COMPONENTS}
          element={<ProductComponentsLayout />}
        >
          <Route
            index
            path={pcfRoutes.PRODUCT_COMPONENTS_INGREDIENTS}
            element={<IngredientsListPage />}
          />

          <Route
            path={pcfRoutes.PRODUCT_COMPONENTS_PROCESSING}
            element={productOnlyRoute(<ProcessingPage />)}
          />
          <Route
            path={pcfRoutes.PRODUCT_COMPONENTS_PROCESSING}
            element={productOnlyRoute(<ProcessingPage />)}
          />
          <Route
            path={pcfRoutes.PRODUCT_COMPONENTS_PACKAGING}
            element={<PackagingListPage />}
          />
          <Route
            path={pcfRoutes.PRODUCT_COMPONENTS_TRANSPORT}
            element={productOnlyRoute(<TransportPage />)}
          />
        </Route>
        <Route
          path={pcfRoutes.PRODUCT_COMPONENTS_INGREDIENTS_DETAIL}
          element={<IngredientDetailContext />}
        >
          <Route path="" element={<IngredientDetailLayout />}>
            <Route index element={<IngredientDetailPage />} />
          </Route>
        </Route>

        <Route path={pcfRoutes.PRODUCT_RESULT} element={<ProductResult />} />
        <Route path={pcfRoutes.PRODUCT_EDIT} element={<EditProductRoute />} />
        <Route path={pcfRoutes.COLLECTIONS} element={<CollectionListPage />} />
        <Route
          path={pcfRoutes.COLLECTION_DETAIL}
          element={<CollectionDetailPage />}
        >
          <Route index element={<CollectionDetailSummary />} />
          <Route
            path={pcfRoutes.COLLECTION_PRODUCTS}
            element={<CollectionDetailProducts />}
          />
          <Route
            path={pcfRoutes.COLLECTION_INGREDIENTS}
            element={<CollectionDetailIngredients />}
          />
        </Route>
        <Route
          path={pcfRoutes.COLLECTION_EDIT}
          element={<EditCollectionPage />}
        />
        <Route
          path={pcfRoutes.COLLECTIONS_CREATE}
          element={<CreateCollectionPage />}
        />

        <Route
          path={pcfRoutes.PRODUCT_COMPARISON}
          element={<ComparisonPage />}
        />
      </Route>

      <Route
        path={pcfRoutes.PRODUCTS_QUICK_IMPORT}
        element={
          <CustomRoute
            feature={[FEATURES.pcf]}
            hasSidebar={false}
            element={<BulkImportPage />}
          />
        }
      />

      <Route
        path={pcfRoutes.PRODUCT_PDF_DOWNLOAD}
        element={
          <CustomRoute
            feature={[FEATURES.pcf]}
            hasSidebar={false}
            element={<ProductPdfDownloadPage />}
          />
        }
      />
    </>
  );
}
